<template>
    <div class="cred-page">
        <!-- A scanned QR is for verification only: no tabs, back link or save/print. -->
        <nav v-if="!verifyMode" class="cred-nav">
            <button type="button" class="cred-back-btn" @click="goBackToProjects">← Back to Projects</button>
            <div class="cred-nav-tabs">
                <button
                    type="button"
                    class="cred-nav-btn"
                    :class="{ active: activeName === 'certificate' }"
                    @click="switchTab('certificate')"
                >Certificate of Achievement</button>
                <button
                    type="button"
                    class="cred-nav-btn"
                    :class="{ active: activeName === 'transcript' }"
                    @click="switchTab('transcript')"
                >Competency Transcript</button>
                <button
                    type="button"
                    class="cred-nav-btn"
                    :class="{ active: activeName === 'letter' }"
                    @click="switchTab('letter')"
                >Recommendation Letter</button>
                <button
                    type="button"
                    class="cred-nav-btn"
                    :class="{ active: activeName === 'portfolio' }"
                    @click="switchTab('portfolio')"
                >Portfolio Page</button>
            </div>
            <div class="cred-nav-actions">
                <button
                    type="button"
                    class="doc-btn cred-nav-print"
                    :disabled="!canPrintCurrent"
                    @click="printCurrentDoc"
                >{{ printButtonLabel }}</button>
            </div>
        </nav>

        <CredentialStatus v-if="activeName !== 'portfolio' && !['ready', 'unissued'].includes(tabStatus[activeName])" :error="tabErrors[activeName]" @retry="ensureTab(activeName)" />
        <section v-if="tabStatus[activeName] === 'unissued'" class="empty-doc-card" role="status">
            <h2>Awaiting issuance</h2>
            <p>This document will be available after the NIM team has confirmed the assessment and reviewed the credential text.</p>
            <button type="button" class="doc-btn" @click="ensureTab(activeName)">Check again</button>
        </section>
        <!-- ═══════════════ Certificate ═══════════════ -->
        <div v-if="tabStatus.certificate === 'ready'" v-show="activeName === 'certificate'" class="cred-view">
            <div class="doc-stage">
                <template v-if="certificateAwards.length > 0">
                    <div id="showTotal" class="doc-print-root">
                        <CertificateDocument
                            v-for="(item, index) in certificateAwards"
                            :key="'cert-' + index"
                            :student-name="studentDisplayName"
                            :project-title="projectInfo.project_title || ''"
                            :track-logo="trackLogo || ''"
                            :track-name="trackDisplayName"
                            :reference="id"
                            :verification-url="certificateUrl"
                            :issue-date="formatCertDate(getCertDateForAward(item))"
                            :award="getAwardDisplay(item)"
                        />
                    </div>
                </template>
                <div v-else class="empty-doc-card">
                    <div class="empty-doc-lbl">No certificate available</div>
                    <p class="noCertificateTip">
                        A certificate is issued when a project is recognised at Honorable Mention level or above. This project has not been recognised at an award level, so there is no certificate to show here.
                    </p>
                    <p class="empty-doc-sub">Your transcript, recommendation letter and portfolio page are available in the other tabs.</p>
                </div>
            </div>
        </div>

        <!-- ═══════════════ Transcript ═══════════════ -->
        <div v-if="tabStatus.transcript === 'ready'" v-show="activeName === 'transcript'" class="cred-view">
            <div id="showTranscript" class="doc-stage">
                    <!-- Page 1 -->
                    <div class="doc-card trans-doc transcript-a4 transcriptPage">
                        <TranscriptBackground />
                        <div class="trans-fill">
                        <div class="ach-hdr">
                            <div class="ach-hdr-bar">
                                <span class="ach-hdr-title">Achievement Summary</span>
                                <span class="ach-hdr-sub">Competency Transcript · Page 1 of {{ competencyPages.length + 1 }}</span>
                            </div>
                        </div>

                        <div class="ach-identity">
                            <div class="ach-identity-main">
                                <div class="ach-name">{{ studentDisplayName }}</div>
                                <div class="ach-meta-line">
                                    <span v-if="trackDisplayName">{{ trackDisplayName }}</span>
                                    <span v-if="academicYearLabel">Academic Year {{ academicYearLabel }}</span>
                                </div>
                            </div>
                            <img
                                v-if="trackLogo"
                                class="ach-hdr-logo"
                                :src="trackLogo"
                                :alt="trackDisplayName"
                            >
                        </div>

                        <div class="trans-stats">
                            <div class="tstat">
                                <div class="tstat-lbl">Total Score</div>
                                <div class="tstat-val">
                                    <template v-if="transcriptStats.full > 0">
                                        {{ transcriptStats.earned }} / {{ transcriptStats.full }} PTS
                                    </template>
                                    <template v-else>{{ transcriptStats.earned }}</template>
                                </div>
                                <div class="tstat-sub">
                                    <template v-if="transcriptStats.pct != null">{{ transcriptStats.pct }}% · </template>
                                    {{ transcriptStats.count }} competencies
                                </div>
                            </div>
                            <div class="tstat">
                                <div class="tstat-lbl">Track</div>
                                <div class="tstat-val tstat-val-sm">{{ trackDisplayName || '—' }}</div>
                                <div class="tstat-sub">{{ programLabel || '—' }}</div>
                            </div>
                            <div class="tstat">
                                <div class="tstat-lbl">Academic Year</div>
                                <div class="tstat-val tstat-val-sm">{{ academicYearLabel || '—' }}</div>
                                <div class="tstat-sub">{{ cycleLabel || '—' }}</div>
                            </div>
                        </div>

                        <div class="trans-body">
                            <div class="tproj">
                                <div class="tproj-main">
                                    <div class="tproj-lbl">Project</div>
                                    <div class="tproj-title">{{ credentialProjectTitle || '—' }}</div>
                                </div>
                            </div>

                            <p class="comp-intro">
                                The following competencies reflect areas of strong performance through assessed
                                work submitted in the course of this project. Each competency is scored as a
                                percentage of the marks available for that dimension
                                <template v-if="trackDisplayName"> in the {{ trackDisplayName }} track</template>.
                            </p>

                            <div class="comp-section">
                                <div class="comp-sec-hdr">
                                    <span class="comp-sec-lbl">Strong Competencies</span>
                                    <div class="comp-sec-rule"></div>
                                    <span class="comp-sec-tag high">
                                        {{ strongCompetencies.length }} of {{ allCompetencies.length || 0 }}
                                        · TOP SCORES
                                    </span>
                                </div>

                                <template v-if="strongCompetencies.length > 0">
                                    <div
                                        v-for="(row, index) in strongCompetencies"
                                        :key="'strong-' + index"
                                        class="comp-row"
                                    >
                                        <div class="comp-row-top">
                                            <span class="comp-name">{{ row.name }}</span>
                                            <span class="comp-score-wrap">
                                                <span class="comp-frac">{{ formatCompetencyScore(row) }}</span>
                                                <span class="comp-pct">{{ competencyPct(row) }}%</span>
                                            </span>
                                        </div>
                                        <div
                                            class="bar-track"
                                            :style="{ maxWidth: barTrackWidth(row) }"
                                        >
                                            <div
                                                class="bar-fill"
                                                :style="{ width: competencyPct(row) + '%' }"
                                            ></div>
                                        </div>
                                    </div>
                                </template>
                                <p v-else class="docPlaceholder">Competency scores are not available yet.</p>

                                <div v-if="strongCompetencies.length > 0" class="bar-legend">
                                    <span class="bar-legend-item">
                                        <span class="bar-legend-swatch track"></span>
                                        Track length = total marks available for this dimension
                                    </span>
                                    <span class="bar-legend-item">
                                        <span class="bar-legend-swatch fill"></span>
                                        Fill = percentage scored within it
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="trans-credits">
                            <div class="tcr-sec-lbl">Member Credits</div>
                            <template v-if="memberCredits">
                                <div class="tcr-name-row">
                                    <span class="tcr-name">{{ studentDisplayName }}</span>
                                    <template v-if="memberRole">
                                        <span class="tcr-sep">—</span>
                                        <span class="tcr-role">{{ memberRole }}</span>
                                    </template>
                                </div>
                                <div ref="memberCreditsParagraph" class="tcr-body">{{ memberCredits }}</div>
                            </template>
                            <p v-else class="docPlaceholder">
                                Member credits for this student are not available yet.
                            </p>
                        </div>
                        </div>

                        <div class="trans-ft brand-doc-ft">
                            <div class="brand-ft-left">
                                <DigitalCertificateQr
                                    :url="transcriptUrl"
                                    size="lg"
                                    :show-label="false"
                                    color-dark="#0B2D6B"
                                />
                                <div class="brand-ft-meta">
                                    <div class="brand-ft-id">{{ id }}</div>
                                    <div class="brand-ft-scan">Scan to verify</div>
                                </div>
                            </div>
                            <div class="brand-ft-right">
                                <div class="brand-ft-date">{{ formatCertDate(transcriptCertDate) }}</div>
                                <div class="brand-ft-issuer">Next Idea Matters</div>
                            </div>
                        </div>
                    </div>

                    <div v-for="(page, pageIndex) in competencyPages" :key="'assessment-' + pageIndex" class="doc-card trans-doc transcript-a4 transcriptPage2">
                        <TranscriptBackground />
                        <div class="trans-fill">
                            <div class="ach-hdr"><div class="ach-hdr-bar">
                                <span class="ach-hdr-title">Achievement Summary</span>
                                <span class="ach-hdr-sub">Competency Transcript · Page {{ pageIndex + 2 }} of {{ competencyPages.length + 1 }}</span>
                            </div></div>
                            <table class="assessment-meta" aria-label="Transcript information">
                                <tbody><tr>
                                    <td><span>Student</span>{{ studentDisplayName }}</td>
                                    <td><span>Track</span>{{ trackDisplayName || '—' }}</td>
                                    <td><span>Total Score</span>{{ transcriptStats.earned }} / {{ transcriptStats.full }} PTS</td>
                                </tr><tr><td colspan="3"><span>Project</span>{{ credentialProjectTitle || '—' }}</td></tr></tbody>
                            </table>
                            <div class="assessment-identity"><strong>Full Competency Assessment</strong></div>
                            <div class="assessment-list">
                                <div v-for="row in page" :key="row.competencyId || row.name" class="assessment-row">
                                    <span>{{ row.name }}</span>
                                    <span>{{ formatCompetencyScore(row) }}</span>
                                    <strong>{{ competencyPct(row) }}%</strong>
                                    <div class="assessment-bar"><div :style="{ width: Math.min(100, competencyPct(row)) + '%' }"></div></div>
                                </div>
                                <p v-if="!page.length">Competency scores are not available yet.</p>
                            </div>
                        </div>
                        <div class="trans-ft brand-doc-ft">
                            <div class="brand-ft-left"><DigitalCertificateQr :url="transcriptUrl" size="lg" :show-label="false" color-dark="#2F509F" />
                                <div class="brand-ft-meta"><div class="brand-ft-id">{{ id }}</div><div class="brand-ft-scan">Scan to verify</div></div>
                            </div>
                            <div class="brand-ft-right"><div class="brand-ft-date">{{ formatCertDate(transcriptCertDate) }}</div><div class="brand-ft-issuer">Next Idea Matters</div></div>
                        </div>
                    </div>
            </div>
        </div>

        <!-- ═══════════════ Letter ═══════════════ -->
        <div v-if="tabStatus.letter === 'ready'" v-show="activeName === 'letter'" class="cred-view">
            <div id="showLetter" class="doc-stage">
                <div class="doc-card letter-doc letter-a4">
                    <svg class="letter-wave-art" viewBox="0 0 794 1123" aria-hidden="true" focusable="false">
                        <path fill="#67C3E8" d="M0 0H794V72C530 18 220 138 0 81Z" />
                        <path fill="#FFCA00" d="M0 0H794V59C530 3 220 122 0 67Z" />
                        <path fill="#F3A600" d="M0 0H794V40C530 -13 220 105 0 49Z" />
                        <path fill="#2F509F" d="M0 0H794V29C530 -25 220 94 0 38Z" />
                        <path fill="#67C3E8" d="M0 1069C230 1131 547 1014 794 1045V1123H0Z" />
                        <path fill="#FFCA00" d="M0 1081C230 1143 547 1028 794 1058V1123H0Z" />
                        <path fill="#F3A600" d="M0 1097C230 1156 547 1042 794 1072V1123H0Z" />
                        <path fill="#2F509F" d="M0 1108C230 1167 547 1053 794 1083V1123H0Z" />
                    </svg>
                    <CredentialBrandLockup class="letter-brand-lockup" :track-logo="trackLogo || ''" :track-name="trackDisplayName" :size="18" unit="mm" />
                    <div class="letter-fill">
                        <div class="letter-title"><h1>A Letter of Recommendation</h1><p>Issued by the Programme Directorate</p></div>
                        <template v-if="recommendationLetter.letterBody">
                            <div class="letter-salutation">
                                {{ recommendationLetter.salutation || 'To Whom It May Concern,' }}
                            </div>

                            <div
                                v-if="recommendationLetter.isHtml"
                                class="letter-html-body"
                                v-html="recommendationLetter.letterBody"
                            ></div>
                            <template v-else>
                                <p
                                    v-for="(para, index) in letterParagraphs"
                                    :key="'lp-' + index"
                                    class="letter-para"
                                >{{ para }}</p>
                            </template>

                            <div class="letter-closing">
                                <div class="letter-closing-text">
                                    {{ recommendationLetter.closing || defaultLetterClosing }}
                                </div>

                            </div>
                        </template>
                        <p v-else class="docPlaceholder">{{ letterEmptyTip }}</p>
                    </div>
                    <footer class="letter-ft-dynamic">
                        <div class="letter-certified-stamp">
                            <div class="letter-certified-title">NIM Certified</div>
                            <DigitalCertificateQr :url="letterUrl" size="lg" />
                        </div>
                        <div class="letter-verification-meta">
                            <div class="letter-ft-date-label">Date of Issue</div>
                            <div class="letter-ft-date-val">{{ formatCertDate(transcriptCertDate) }}</div>
                            <div class="letter-ft-id">Verification ID: {{ id }}</div>
                        </div>
                    </footer>
                </div>
            </div>
        </div>

        <!-- ═══════════════ Portfolio ═══════════════ -->
        <div v-show="activeName === 'portfolio'" class="cred-view cred-portfolio-view">
            <PortfolioDocument v-if="portfolioVisited" ref="portfolioDocument" :key="id" embedded :application-id="id" @load-state="portfolioStatus = $event" />
        </div>
    </div>
</template>

<script>
import { compressMemberCreditsForTranscript } from "../common/competencyTranscript"
import {
    getStudentAwardsByApplication,
    getStudentCompetencyTranscriptByApplication,
    getStudentRecommendationLetterByApplication,
    getApplicationByID
} from '../service'
import CredentialStatus from './CredentialStatus.vue'
import DigitalCertificateQr from './DigitalCertificateQr.vue'
import PortfolioDocument from './portfolio.vue'
import { filterCertificateAwards, getAwardDisplay } from '../common/certificateAwards'
import { resolveCertDateFromSubmission } from '../common/applicationCycle'
import {
    buildCompetencyTranscriptView,
    formatCompetencyScore as formatCompetencyScoreValue,
    sortCompetenciesByScore,
    getTopCompetencies,
    TOP_COMPETENCY_COUNT
} from '../common/competencyTranscript'
import { normalizeRecommendationLetter } from '../common/recommendationLetter'
import { resolveInnovationName } from '../common/nimTrack'
import CredentialBrandLockup from './CredentialBrandLockup.vue'
import nimWordmark from '../assets/nim-wordmark.png'
import CertificateDocument from './CertificateDocument.vue'
import TranscriptBackground from './TranscriptBackground.vue'

const STRONG_THRESHOLD = 0.75

export default {
    name: 'index',
    components: {
        CertificateDocument,
        TranscriptBackground,
        CredentialBrandLockup,
        CredentialStatus,
        DigitalCertificateQr,
        PortfolioDocument
    },
    data() {
        return {
            id: this.$route.query.id,
            activeName: this.normalizeTab(this.$route.query.tab),
            tabStatus: { certificate: 'idle', transcript: 'idle', letter: 'idle' },
            tabErrors: { certificate: '', transcript: '', letter: '' },
            portfolioVisited: false,
            portfolioStatus: 'idle',
            awards: [],
            projectInfo: {},
            studentInfo: {},
            accountInfo: {},
            projectManage: {},
            applicationCycle: '',
            submissionDate: '',
            credentialEpoch: 0,
            publishedCredentials: { transcript: null, letter: null },
            selectedCredentialVersion: this.$route.query.version || '',
            memberCredits: '',
            originalMemberCredits: '',
            memberCreditsWordBudget: 100,
            memberRole: '',
            trackName: '',
            allCompetencies: [],
            topCompetencies: [],
            transcriptTotalEarned: null,
            transcriptTotalFull: null,
            recommendationLetter: normalizeRecommendationLetter({}),
            nimWordmark
        }
    },
    computed: {
        activePublishedCredential() { return this.publishedCredentials[this.activeName] || null },
        // A scanned QR opens only the document it is printed on (like EAE public certificates).
        verifyMode() { return this.$route.query.view === 'verify' },
        credentialProjectTitle() { return this.activePublishedCredential ? this.activePublishedCredential.project_title : this.projectInfo.project_title },
        certificateAwards() {
            return filterCertificateAwards(this.awards).slice(0, 1)
        },
        primaryAward() {
            return this.certificateAwards.length ? this.certificateAwards[0] : null
        },
        studentDisplayName() {
            if (this.activePublishedCredential) return this.activePublishedCredential.student_name
            const given = this.upfirstName(this.studentInfo.student_givenName_pinyin || this.studentInfo.student_givenName)
            const last = this.upfirstName(this.studentInfo.student_lastName_pinyin || this.studentInfo.student_lastName)
            return [given, last].filter(Boolean).join(' ')
        },
        trackDisplayName() {
            if (this.activePublishedCredential) return this.activePublishedCredential.track_name
            return this.trackName ||
                this.projectInfo.project_product ||
                this.projectInfo.track_name ||
                this.projectInfo.project_ename ||
                ''
        },
        trackLogo() {
            if (this.activePublishedCredential) return this.resolveTrackLogo(this.activePublishedCredential.track_code)
            return this.resolveTrackLogo(
                this.projectInfo.project_code ||
                this.projectInfo.project_product ||
                this.trackName ||
                this.projectInfo.project_ename ||
                ''
            )
        },
        programLabel() {
            if (this.activePublishedCredential) return resolveInnovationName(this.activePublishedCredential.track_code)
            return resolveInnovationName(
                this.projectInfo.project_code ||
                this.projectInfo.project_product ||
                this.trackName ||
                this.projectInfo.project_ename ||
                ''
            )
        },
        academicYearLabel() {
            const date = this.transcriptCertDate ? new Date(this.transcriptCertDate) : null
            if (!date || isNaN(date.getTime())) {
                return ''
            }
            const year = date.getFullYear()
            const month = date.getMonth()
            // Academic year spans Aug–Jul
            if (month >= 7) {
                return year + '–' + (year + 1)
            }
            return (year - 1) + '–' + year
        },
        cycleLabel() {
            const cycles = (this.projectManage && this.projectManage.entry_date) || []
            if (this.applicationCycle && this.applicationCycle !== 'Normal Review') {
                return this.applicationCycle
            }
            if (!cycles.length) {
                return ''
            }
            const certDate = this.transcriptCertDate
            let matched = null
            if (certDate) {
                matched = cycles.find(c => c && c.cert_date === certDate)
            }
            const cycle = matched || cycles[cycles.length - 1]
            return (cycle && (cycle.cycle_name || cycle.name || cycle.label)) || ''
        },
        transcriptStats() {
            const apiFull = Number(this.transcriptTotalFull)
            const apiEarned = Number(this.transcriptTotalEarned)
            if (apiFull > 0) {
                const pct = Math.round((apiEarned / apiFull) * 1000) / 10
                return {
                    earned: Math.round(apiEarned * 10) / 10,
                    full: Math.round(apiFull * 10) / 10,
                    pct,
                    count: (this.allCompetencies || []).length
                }
            }
            const list = this.allCompetencies || []
            const earned = list.reduce((sum, row) => sum + (Number(row.studentScore) || 0), 0)
            const full = list.reduce((sum, row) => sum + (Number(row.fullScore) || 0), 0)
            const pct = full > 0 ? Math.round((earned / full) * 1000) / 10 : null
            return {
                earned: Math.round(earned * 10) / 10,
                full: Math.round(full * 10) / 10,
                pct,
                count: list.length
            }
        },
        maxCompetencyFullScore() {
            const list = this.allCompetencies || []
            let max = 0
            list.forEach(row => {
                if (row.fullScore > max) {
                    max = row.fullScore
                }
            })
            return max || 1
        },
        competencyPages() {
            const rows = sortCompetenciesByScore(this.allCompetencies || [])
            const pages = []; let page = []; let height = 0
            const canvas = document.createElement('canvas'); const context = canvas.getContext('2d')
            context.font = '9pt Arial'
            rows.forEach(row => {
                let lines = 1; let line = ''
                String(row.name || '').split(/\s+/).forEach(word => {
                    const next = line ? line + ' ' + word : word
                    if (context.measureText(next).width > 490 && line) { lines++; line = word } else line = next
                })
                const rowHeight = 32 + (lines - 1) * 16
                if (page.length && (page.length >= 20 || height + rowHeight > 640)) { pages.push(page); page = []; height = 0 }
                page.push(row); height += rowHeight
            })
            if (page.length) pages.push(page)
            return pages.length ? pages : [[]]
        },
        strongCompetencies() { return getTopCompetencies(this.allCompetencies || [], TOP_COMPETENCY_COUNT) },
        highCompetencies() {
            return sortCompetenciesByScore(this.allCompetencies || [])
                .filter(row => this.competencyRatio(row) >= STRONG_THRESHOLD)
        },
        lowCompetencies() {
            return sortCompetenciesByScore(this.allCompetencies || [])
                .filter(row => this.competencyRatio(row) < STRONG_THRESHOLD)
        },
        letterParagraphs() {
            if (this.recommendationLetter.isHtml) {
                return []
            }
            if (this.recommendationLetter.paragraphs && this.recommendationLetter.paragraphs.length) {
                return this.recommendationLetter.paragraphs
            }
            const body = this.recommendationLetter.letterBody || ''
            return body ? [body] : []
        },
        defaultLetterClosing() {
            const name = this.studentDisplayName || 'this student'
            return 'We are pleased to recognise ' + name + '’s contribution to the work presented here.'
        },
        letterIssueYear() {
            const date = this.transcriptCertDate ? new Date(this.transcriptCertDate) : new Date()
            const year = date.getFullYear()
            return isNaN(year) ? new Date().getFullYear() : year
        },
        letterEmptyTip() {
            if (this.recommendationLetter.generationStatus === 'pending') {
                return 'Recommendation letter is being prepared from this student\'s project contributions.'
            }
            if (this.recommendationLetter.generationStatus === 'failed') {
                return 'AI recommendation letter generation failed. Please try again later.'
            }
            return 'Recommendation letter content is not available yet.'
        },
        // Transcript and letter carry the same date as the certificate: the cycle's configured
        // cert_date, not the moment the credential version was issued.
        transcriptCertDate() {
            if (this.primaryAward) {
                return this.getCertDateForAward(this.primaryAward)
            }
            const entryDate = (this.projectManage && this.projectManage.entry_date) || []
            const certDate = resolveCertDateFromSubmission(entryDate, this.submissionDate)
            if (certDate) {
                return certDate
            }
            if (!entryDate.length) {
                return undefined
            }
            const sortedCycles = entryDate.slice().sort((a, b) => {
                const ad = a && a.ddl_date ? a.ddl_date : 0
                const bd = b && b.ddl_date ? b.ddl_date : 0
                return ad - bd
            })
            return sortedCycles[sortedCycles.length - 1] && sortedCycles[sortedCycles.length - 1].cert_date
        },
        certificateUrl() {
            return this.publicVerifyUrl('certificate')
        },
        transcriptUrl() {
            return this.publicVerifyUrl('transcript')
        },
        letterUrl() {
            return this.publicVerifyUrl('letter')
        },
        canPrintCurrent() {
            if (this.activeName === 'portfolio') return this.portfolioStatus === 'ready'
            if (this.tabStatus[this.activeName] !== 'ready') return false
            if (this.activeName === 'certificate') {
                return this.certificateAwards.length > 0
            }
            if (this.activeName === 'letter') {
                return !!(this.recommendationLetter && this.recommendationLetter.letterBody)
            }
            return true
        },
        printButtonLabel() {
            if (this.activeName === 'portfolio') return 'Share'
            if (this.activeName === 'transcript') {
                return '↓ Save / Print (all pages)'
            }
            return '↓ Save / Print'
        }
    },
    updated() { this.fitMemberCredits() },
    mounted() {
        this.ensureTab(this.activeName)
        this.updatePrintPage()
    },
    watch: {
        '$route.query.id'(id) {
            if (id === this.id) return
            this.credentialEpoch++; this.id = id; this.credentialBaseRequest = null
            this.publishedCredentials = { transcript: null, letter: null }; this.selectedCredentialVersion = this.$route.query.version || ''
            this.tabStatus = { certificate: 'idle', transcript: 'idle', letter: 'idle' }
            this.tabErrors = { certificate: '', transcript: '', letter: '' }
            this.portfolioStatus = 'idle'; this.portfolioVisited = false
            this.awards = []; this.studentInfo = {}; this.projectInfo = {}; this.projectManage = {}
            this.memberCredits = ''; this.originalMemberCredits = ''; this.allCompetencies = []; this.trackName = ''
            this.recommendationLetter = normalizeRecommendationLetter({})
            this.ensureTab(this.normalizeTab(this.$route.query.tab))
        },
        '$route.query.version'(version) {
            this.credentialEpoch++
            this.selectedCredentialVersion = version || ''
            this.publishedCredentials = { transcript: null, letter: null }
            this.tabStatus.transcript = 'idle'; this.tabStatus.letter = 'idle'
            this.ensureTab(this.activeName)
        },
        '$route.query.tab'(tab) { this.activeName = this.normalizeTab(tab); this.ensureTab(this.activeName); this.updatePrintPage() }
    },
    beforeDestroy() {
        if (this.printPageStyle) this.printPageStyle.remove()
    },
    methods: {
        updatePrintPage() {
            if (!this.printPageStyle) {
                this.printPageStyle = document.createElement('style')
                this.printPageStyle.setAttribute('data-nim-print-page', '')
                this.printPageStyle.media = 'print'
                document.head.appendChild(this.printPageStyle)
            }
            this.printPageStyle.textContent = '@page { size: A4 ' + (this.activeName === 'certificate' ? 'landscape' : 'portrait') + '; margin: 0; }'
        },
        async ensureTab(tab) {
            if (tab === 'portfolio') { this.portfolioVisited = true; return }
            if (this.tabStatus[tab] === 'ready' || this.tabStatus[tab] === 'loading') return
            const applicationId = this.id
            const epoch = this.credentialEpoch
            this.$set(this.tabStatus, tab, 'loading')
            this.$set(this.tabErrors, tab, '')
            try {
                await this.fetchData()
                if (applicationId !== this.id || epoch !== this.credentialEpoch) return
                if (tab === 'transcript') await this.fetchCompetencyTranscript()
                if (tab === 'letter') await this.fetchRecommendationLetter()
                if (applicationId !== this.id || epoch !== this.credentialEpoch) return
                this.$set(this.tabStatus, tab, ['transcript', 'letter'].includes(tab) && !this.publishedCredentials[tab] ? 'unissued' : 'ready')
            } catch (error) {
                if (applicationId !== this.id || epoch !== this.credentialEpoch) return
                this.$set(this.tabErrors, tab, 'Please try again. Your document could not be retrieved.')
                this.$set(this.tabStatus, tab, 'error')
            }
        },
        credentialPayload(response) {
            if (!response || !response.data || response.data.code !== 0 || !response.data.data) throw new Error('Credential request failed')
            return response.data.data
        },
        fitMemberCredits() {
            if (this.publishedCredentials.transcript) return
            const paragraph = this.$refs.memberCreditsParagraph
            if (!paragraph || !paragraph.clientWidth || !this.originalMemberCredits) return
            const lineHeight = parseFloat(window.getComputedStyle(paragraph).lineHeight)
            if (paragraph.scrollHeight <= lineHeight * 5 + 1 || this.memberCreditsWordBudget <= 35) return
            this.memberCreditsWordBudget -= 5
            this.memberCredits = compressMemberCreditsForTranscript(this.originalMemberCredits, this.memberCreditsWordBudget)
        },
        getAwardDisplay,
        printCurrentDoc() {
            if (this.activeName === 'portfolio') { this.$refs.portfolioDocument.copyPublicLink(); return }
            if (!this.canPrintCurrent) {
                return
            }
            this.exportPDF()
        },
        resolveTrackLogo(codeOrName) {
            const raw = String(codeOrName || '').toUpperCase()
            if (!raw) {
                return ''
            }
            const map = {
                NSI: require('../assets/nsi_logo.png'),
                NRI: require('../assets/nri_logo.png'),
                NIR: require('../assets/nir_logo.png'),
                NIP: require('../assets/nip_logo.png'),
                NIA: require('../assets/nia_logo.png'),
                NIE: require('../assets/nie_logo.png'),
                NII: require('../assets/nii_logo.png'),
                NIV: require('../assets/niv_logo.png')
            }
            const keys = Object.keys(map)
            for (let i = 0; i < keys.length; i++) {
                const key = keys[i]
                if (raw.indexOf(key) >= 0) {
                    return map[key]
                }
            }
            return ''
        },
        formatCompetencyScore(row) {
            return formatCompetencyScoreValue(row)
        },
        competencyRatio(row) {
            if (!row) {
                return 0
            }
            if (row.fullScore > 0) {
                return row.studentScore / row.fullScore
            }
            return Number(row.scoreRatio) || 0
        },
        competencyPct(row) {
            return Math.round(this.competencyRatio(row) * 100)
        },
        barTrackWidth(row) {
            const full = (row && row.fullScore) || 0
            const max = this.maxCompetencyFullScore || 1
            const pct = Math.max(18, Math.round((full / max) * 100))
            return pct + '%'
        },
        formatCertDate(publish_time) {
            return this.formatCertDateValue(publish_time)
        },
        normalizeTab(tab) {
            if (tab === 'transcript' || tab === 'letter' || tab === 'certificate' || tab === 'portfolio') {
                return tab
            }
            return 'certificate'
        },
        goBackToProjects() {
            this.$router.push({ path: '/studentProjects' }).catch(() => {})
        },
        switchTab(tab) {
            const next = this.normalizeTab(tab)
            this.activeName = next
            this.ensureTab(next)
            this.updatePrintPage()
            if (this.$route.query.tab !== next) {
                this.$router.replace({
                    query: Object.assign({}, this.$route.query, { tab: next })
                }).catch(() => {})
            }
        },
        publicVerifyUrl(tab) {
            const origin = window.location.origin
            const path = window.location.pathname || '/'
            return origin + path + '#/cert?id=' + encodeURIComponent(this.id || '') +
                '&tab=' + encodeURIComponent(tab) + '&view=verify' +
                (this.publishedCredentials[tab] ? '&version=' + encodeURIComponent(this.publishedCredentials[tab].credential_version_id) : '')
        },
        getCertDateForAward(item) {
            const cycles = (this.projectManage && this.projectManage.entry_date) ? this.projectManage.entry_date : []
            if (!cycles || cycles.length === 0) {
                return undefined
            }

            let awardTimeMs = item && item.create_time ? item.create_time : 0
            if (awardTimeMs < 1000000000000) {
                awardTimeMs = awardTimeMs * 1000
            }

            const sortedCycles = cycles.slice().sort((a, b) => {
                const ad = a && a.ddl_date ? a.ddl_date : 0
                const bd = b && b.ddl_date ? b.ddl_date : 0
                return ad - bd
            })

            for (let i = 0; i < sortedCycles.length; i++) {
                const cycle = sortedCycles[i]
                const ddl = cycle && cycle.ddl_date ? cycle.ddl_date : 0
                if (awardTimeMs < ddl) {
                    const prevIndex = i - 1
                    if (prevIndex >= 0) {
                        return sortedCycles[prevIndex] && sortedCycles[prevIndex].cert_date
                    }
                    return sortedCycles[0] && sortedCycles[0].cert_date
                }
            }

            return sortedCycles[sortedCycles.length - 1] && sortedCycles[sortedCycles.length - 1].cert_date
        },
        // Cycle dates are configured as Beijing calendar days (midnight UTC+8); read them in that
        // zone so viewers abroad do not see the previous day.
        formatCertDateValue(publish_time) {
            const date = new Date(typeof publish_time === 'string' && /^\d+$/.test(publish_time) ? Number(publish_time) : publish_time)
            if (publish_time === undefined || publish_time === null || publish_time === '' || isNaN(date.getTime())) {
                return ''
            }
            const beijing = new Date(date.getTime() + 8 * 60 * 60 * 1000)
            return this.formatMonthToEng(beijing.getUTCMonth() + 1) + ' ' + beijing.getUTCDate() + ', ' + beijing.getUTCFullYear()
        },
        formatMonthToEng(month) {
            switch (month) {
                case 1: return 'January'
                case 2: return 'February'
                case 3: return 'March'
                case 4: return 'April'
                case 5: return 'May'
                case 6: return 'June'
                case 7: return 'July'
                case 8: return 'August'
                case 9: return 'September'
                case 10: return 'October'
                case 11: return 'November'
                case 12: return 'December'
            }
        },
        fetchData() {
            if (!this.credentialBaseRequest) {
                const applicationId = this.id
                this.credentialBaseRequest = Promise.all([getApplicationByID(this.id), getStudentAwardsByApplication(this.id)]).then(([applicationResponse, awardsResponse]) => {
                    if (applicationId !== this.id) return
                    const application = this.credentialPayload(applicationResponse)
                    const data = this.credentialPayload(awardsResponse)
                    this.submissionDate = application.submission_date || ''
                    this.applicationCycle = application.application_cycle || ''
                    this.awards = data.awards || []
                    this.studentInfo = data.student_info || {}
                    this.accountInfo = data.account_info || {}
                    this.projectInfo = data.project_info || {}
                    this.projectManage = data.projectManage || {}
                }).catch(error => { if (applicationId === this.id) this.credentialBaseRequest = null; throw error })
            }
            return this.credentialBaseRequest
        },
        async fetchPublishedCredential(tab, api) {
            const applicationId = this.id; const epoch = this.credentialEpoch
            const requestedVersion = this.selectedCredentialVersion
            let data = this.credentialPayload(await api(applicationId, requestedVersion))
            if (applicationId !== this.id || epoch !== this.credentialEpoch) return null
            // Two tabs opened together must share the first returned issued version.
            if (this.selectedCredentialVersion && data.credential_version_id !== this.selectedCredentialVersion && requestedVersion !== this.selectedCredentialVersion) {
                data = this.credentialPayload(await api(applicationId, this.selectedCredentialVersion))
                if (applicationId !== this.id || epoch !== this.credentialEpoch) return null
            }
            if (data.credential_status !== 'issued') { this.$set(this.publishedCredentials, tab, null); return null }
            this.selectedCredentialVersion = data.credential_version_id
            this.$set(this.publishedCredentials, tab, data)
            return data
        },
        async fetchCompetencyTranscript() {
            const data = await this.fetchPublishedCredential('transcript', getStudentCompetencyTranscriptByApplication)
            if (!data) return
            const view = buildCompetencyTranscriptView(data, { projectInfo: this.projectInfo, studentInfo: this.studentInfo })
            this.originalMemberCredits = view.originalMemberCredits || ''
            this.memberCreditsWordBudget = 100
            this.memberCredits = view.memberCredits || ''
            this.memberRole = view.memberRole || ''
            this.trackName = view.trackName
            this.allCompetencies = view.allCompetencies
            this.topCompetencies = view.topCompetencies
            this.transcriptTotalEarned = view.totalStudentScore
            this.transcriptTotalFull = view.totalFullScore
        },
        async fetchRecommendationLetter() {
            const data = await this.fetchPublishedCredential('letter', getStudentRecommendationLetterByApplication)
            if (!data) return
            this.recommendationLetter = normalizeRecommendationLetter(data, {
                studentName: this.studentDisplayName,
                projectTitle: this.projectInfo.project_title || '',
                trackName: this.trackDisplayName,
                primaryAward: this.primaryAward,
                awards: this.certificateAwards
            })
        },
        upfirstName(name) {
            if (!name) {
                return ''
            }
            if (name == 'João Pedro') {
                return name
            }
            if (name == 'AGYEN-FREMPONG') {
                return name
            }
            return name.toLowerCase().replace(/\b([\w|‘]+)\b/g, function (word) {
                return word.replace(word.charAt(0), word.charAt(0).toUpperCase())
            })
        },
        exportPDF() {
            this.updatePrintPage()
            window.print()
        }
    }
}
</script>

<style lang="scss">
/* Isolate credentials page from App.vue globals (#app text-align/margin/font).
   Chrome tokens follow NIM Partner Center / credentials draft (incl. dark theme). */

/* Document backgrounds: lock ratios so supplied artwork is not stretched. */
.brand-frame {
    aspect-ratio: 297 / 210;
    width: 100%;
    max-width: 980px;
    min-height: 0;
    height: auto;
    background-color: #FFFFFF;
    display: flex;
    flex-direction: column;
    position: relative;
}

.doc-bg-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: fill;
    object-position: center top;
    pointer-events: none;
    user-select: none;
    z-index: 0;
    display: block;
}

.cert-doc {
    .cert-logo-track {
        position: absolute;
        top: 1.15rem;
        right: 1.35rem;
        z-index: 3;
        height: 28px;
        width: auto;
        max-width: 140px;
        object-fit: contain;
        background: #FFF;
        padding: 4px 6px;
        border-radius: 3px;
    }

    .cert-inner {
        position: relative;
        z-index: 1;
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 3.1rem 3.2rem 2.6rem;
    }
}

.trans-doc {
    position: relative;
    z-index: 0;
    border: none;
}

.trans-doc.transcript-a4 {
    width: 210mm;
    height: 297mm;
    max-width: 210mm !important;
    min-height: 297mm;
    aspect-ratio: auto;
    margin: 0 auto;
    overflow: hidden;
    background-color: #FFFFFF;
    position: relative;
    display: block;
    box-sizing: border-box;
    padding: 0;

    .doc-bg-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: fill;
        object-position: center top;
        pointer-events: none;
        user-select: none;
        z-index: 0;
        display: block;
    }

    .trans-fill {
        position: absolute;
        z-index: 1;
        left: 5.5%;
        right: 5.5%;
        top: 5.5%;
        bottom: 15%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        padding: 0 2mm;
    }

    .ach-hdr {
        padding: 0.35rem 0 0.55rem;
        margin: 0;
        flex-shrink: 0;
    }

    .ach-hdr-bar {
        background: transparent;
        color: #FFF;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
        width: 100%;
        box-sizing: border-box;
    }

    .ach-identity {
        padding: 1rem 0 0.55rem;
    }

    .trans-stats {
        padding-left: 0;
        padding-right: 0;
        margin: 0.85rem 0 0.75rem;
    }

    .trans-body {
        flex: 1;
        min-height: 0;
        overflow: hidden;
        padding-left: 0;
        padding-right: 0;
    }

    .trans-credits {
        padding-left: 0;
        padding-right: 0;
        margin-bottom: 0.5rem;
    }

    .trans-ft {
        position: absolute;
        left: 5.5%;
        right: 5.5%;
        bottom: 3.5%;
        z-index: 2;
        margin: 0;
        flex-shrink: 0;
        padding: 0.8rem 2mm 0;
        border-top: 1px solid var(--line);
        background: transparent;
        box-sizing: border-box;
        align-items: flex-start;
    }

    .brand-ft-track {
        height: 26px;
        max-width: 120px;
    }

    .brand-ft-id {
        font-size: 8pt;
        font-weight: 400;
        text-align: center;
        color: #6D7683;
    }

    .brand-ft-scan {
        font-size: 6.5pt;
    }

    .brand-ft-date {
        font-size: 10pt;
    }
}

.letter-doc.letter-a4 {
    /* Fixed A4 — matches print @page size */
    width: 210mm;
    height: 297mm;
    max-width: 210mm !important;
    min-height: 297mm;
    aspect-ratio: auto;
    margin: 0 auto;
    border: none;
    overflow: hidden;
    background-color: #FFFFFF;
    position: relative;
    display: block;
    box-sizing: border-box;

    .doc-bg-img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: fill;
        object-position: center top;
        z-index: 0;
    }

    .letter-fill {
        position: absolute;
        z-index: 1;
        left: 12%;
        right: 12%;
        top: 20%;
        bottom: 19%;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
    }

    .letter-salutation {
        margin: 0 0 0.85rem;
        flex-shrink: 0;
        font-size: 11pt;
        line-height: 1.35;
        font-weight: 600;
        color: var(--navy);
    }

    .letter-html-body,
    .letter-para {
        flex-shrink: 0;
        font-family: var(--serif);
        font-size: 10.5pt;
        line-height: 1.62;
        margin: 0 0 0.95rem;
        color: var(--ink);
        text-align: justify;
    }

    .letter-html-body .letter-para {
        font-size: 10.5pt;
        line-height: 1.62;
        margin: 0 0 0.95rem;
    }

    .letter-closing {
        margin-top: 0.5rem;
        flex-shrink: 0;
    }

    .letter-closing-text {
        font-family: var(--serif);
        font-size: 10.5pt;
        line-height: 1.5;
        margin-bottom: 1rem;
        color: var(--navy);
    }

    .letter-sig-rule {
        width: 100px;
        margin-bottom: 0.3rem;
    }

    .letter-sig-name {
        font-size: 9pt;
    }

    .letter-sig-org {
        font-size: 8pt;
    }
}

.cred-page {
    --sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    --serif: Georgia, "Times New Roman", serif;
    --mono: "SF Mono", "Fira Code", Consolas, "Courier New", monospace;
    --navy: #0B2D6B;
    --blue: #1A4F8C;
    --orange: #F5B400;
    --teal: #178C80;
    --gold: #F5B400;
    --track: #F5B400;
    --white: #FFFFFF;
    --ink: #172033;
    --ink-2: #5B6574;
    --ink-3: #526072;
    --ink-4: #83909E;
    --surf: #E9EEF3;
    --surf-2: #EEF3F7;
    --bdr: #CBD5DF;
    --line: #E6EAF0;
    --wash: #F5F8FC;
    --sc-h: #0B2D6B;
    --sc-hbg: #E4EEF8;
    --sc-l: #526072;
    --sc-lbg: #EEF1F5;

    box-sizing: border-box;
    margin: -60px 0 0;
    min-height: calc(100vh + 60px);
    background: var(--surf);
    color: var(--ink);
    font-family: var(--sans) !important;
    text-align: left !important;
    -webkit-font-smoothing: antialiased;
}

@media (prefers-color-scheme: dark) {
    .cred-page {
        --navy: #0A1E33;
        --blue: #4A90D8;
        --orange: #F5A623;
        --teal: #16A085;
        --gold: #F5A623;
        --track: #F5A623;
        --white: #0D1830;
        --ink: #D8E6F4;
        --ink-2: #98ACCB;
        --ink-3: #627A92;
        --ink-4: #3E5265;
        --surf: #0A1525;
        --surf-2: #162038;
        --bdr: #1E3054;
        --line: #1E3054;
        --wash: #0F1A2A;
        --sc-h: #4A90D8;
        --sc-hbg: #0F2240;
        --sc-l: #455870;
        --sc-lbg: #0F1A2A;
    }
}

.cred-page *,
.cred-page *::before,
.cred-page *::after {
    box-sizing: border-box;
}

.cred-nav {
    background: var(--white);
    border-bottom: 1px solid var(--line);
    height: 48px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    padding: 0 1.5rem;
    position: sticky;
    top: 0;
    z-index: 200;
}

.cred-back-btn {
    appearance: none;
    border: none;
    background: transparent;
    font-size: 0.625rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: none;
    color: var(--navy);
    justify-self: start;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
}

.cred-nav-tabs {
    display: flex;
    align-items: stretch;
    justify-self: center;
    height: 48px;
}

.cred-nav-actions {
    justify-self: end;
    display: flex;
    align-items: center;
    min-width: 13.5rem;
}

.cred-nav-btn {
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    height: 48px;
    padding: 0 1.1rem;
    font: 500 0.7875rem / 1 var(--sans);
    color: var(--ink-3);
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: color 0.15s, border-color 0.15s;
    white-space: nowrap;
    flex-shrink: 0;

    &:hover {
        color: var(--ink);
    }

    &.active {
        color: var(--ink);
        border-bottom-color: var(--orange);
    }
}

.cred-nav-print {
    margin-left: 0;
    flex-shrink: 0;
    min-width: 13.5rem;
    text-align: center;
    background: var(--white) !important;
    border: 1px solid var(--bdr) !important;
    border-radius: 3px !important;
    padding: 0.35rem 0.875rem !important;
    height: auto !important;
    font: 500 0.7rem / 1 var(--sans) !important;
    color: var(--ink-2) !important;
    letter-spacing: 0.02em;
    box-shadow: none;

    &:hover:not(:disabled) {
        border-color: var(--orange) !important;
        color: var(--ink) !important;
    }

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
}

@media (max-width: 900px) {
    .cred-nav {
        padding: 0 1rem;
        grid-template-columns: minmax(0, 1fr) auto minmax(8.5rem, 1fr);
    }

    .cred-back-btn {
        display: none;
    }

    .cred-nav-actions {
        min-width: 8.5rem;
    }

    .cred-nav-print {
        min-width: 8.5rem;
        padding: 0.35rem 0.5rem !important;
        font-size: 0.65rem !important;
    }

    .cred-nav-btn {
        padding: 0 0.65rem;
        font-size: 0.72rem;
    }
}

.cred-view {
    width: 100%;
}

.doc-btn {
    background: var(--white);
    border: 1px solid var(--bdr);
    border-radius: 3px;
    padding: 0.35rem 0.875rem;
    font: 500 0.7rem / 1 var(--sans);
    color: var(--ink-2);
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s;

    &:hover {
        border-color: var(--orange);
        color: var(--ink);
    }
}

.doc-stage {
    min-height: calc(100vh - 48px);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 1.5rem 1rem 5rem;
    gap: 1rem;
    background: var(--surf);
    text-align: left;
}

.doc-print-root {
    width: 100%;
    max-width: 1120px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
}

.doc-card {
    width: 100%;
    max-width: 724px;
    background-color: #FFFFFF;
    /* Documents always render on white paper — pin light palette */
    --navy: #0B2D6B;
    --blue: #1A4F8C;
    --orange: #F5B400;
    --cyan: #5EC8E6;
    --teal: #178C80;
    --gold: #F5B400;
    --track: #F5B400;
    --ink: #172033;
    --ink-2: #5B6574;
    --ink-3: #526072;
    --ink-4: #83909E;
    --line: #E6EAF0;
    --wash: #F5F8FC;
    --sc-h: #0B2D6B;
    --sc-hbg: #E4EEF8;
    --sc-l: #526072;
    --sc-lbg: #EEF1F5;
    box-shadow: 0 2px 8px rgba(22, 32, 51, 0.10), 0 18px 52px rgba(22, 32, 51, 0.16);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    text-align: left;
    position: relative;
    overflow: hidden;
}

.brand-frame-bg {
    display: none;
}

.brand-wordmark {
    font-family: var(--sans);
    font-weight: 800;
    font-size: 0.92rem;
    letter-spacing: 0.08em;
    color: var(--navy);
    line-height: 1.35;
    text-transform: uppercase;

    span {
        position: relative;
        display: inline-block;
        padding-top: 0.3em;

        &::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 0;
            width: 0;
            height: 0;
            border-left: 0.2em solid transparent;
            border-right: 0.2em solid transparent;
            border-bottom: 0.26em solid currentColor;
            transform: translateX(-50%);
        }
    }
}

.brand-doc-ft {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: auto;
    padding-top: 1.25rem;
    border-top: 1px solid var(--line);
}

.brand-ft-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    max-width: 70%;
}

.brand-ft-meta {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    min-width: 0;
}

.brand-ft-track {
    height: 28px;
    width: auto;
    max-width: 150px;
    object-fit: contain;
    object-position: left center;
    display: block;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
}

.brand-ft-id {
    font: 700 0.62rem / 1.2 var(--mono);
    color: var(--navy);
    letter-spacing: 0.04em;
}

.brand-ft-scan {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-4);
}

.brand-ft-right {
    text-align: right;
}

.brand-ft-lbl {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.2rem;
}

.brand-ft-date {
    font: 600 0.95rem / 1.2 var(--sans);
    color: var(--navy);
}

.brand-ft-issuer {
    margin-top: 0.2rem;
    font-size: 0.7rem;
    color: var(--ink-3);
}

.ach-hdr {
    display: block;
    padding: 0;
    margin: 0;
}

.ach-hdr-bar {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 0.12rem;
    background: transparent;
    color: #FFF;
    padding: 0.55rem 28% 0.55rem 0;
    border-radius: 0;
    max-width: none;
    width: 100%;
    box-sizing: border-box;
}

.ach-hdr-title {
    font-size: 1.12rem;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    line-height: 1.2;
}

.ach-hdr-sep {
    display: none;
}

.ach-hdr-sub {
    display: block;
    font-size: 0.5rem;
    font-weight: 400;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.85;
    line-height: 1.2;
}

.ach-hdr-logo {
    height: 56px;
    width: auto;
    flex-shrink: 0;
    object-fit: contain;
}

.ach-identity {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 0 0.55rem;
}

.ach-identity-main {
    min-width: 0;
    flex: 1;
}

.ach-name {
    font-family: var(--sans);
    font-weight: 800;
    font-size: clamp(1.55rem, 2.8vw, 1.95rem);
    color: var(--navy);
    line-height: 1.15;
    letter-spacing: -0.01em;
}

.ach-meta-line {
    margin-top: 0.45rem;
    display: flex;
    flex-wrap: wrap;
    gap: 1.1rem;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink);
}

.letter-h1 {
    margin: 0.35rem 0 0.75rem;
    font-family: var(--sans);
    font-weight: 800;
    font-size: clamp(1.45rem, 2.6vw, 1.85rem);
    color: var(--navy);
    letter-spacing: -0.01em;
}

.letter-banner,
.cert-banner {
    display: inline-block;
    background: var(--orange);
    color: #FFF;
    font-size: 0.58rem;
    font-weight: 800;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    padding: 0.42rem 0.9rem;
}

.empty-doc-card {
    width: 100%;
    max-width: 720px;
    background: #FFFFFF;
    --ink: #172033;
    --ink-2: #5B6574;
    --ink-3: #526072;
    --ink-4: #83909E;
    --bdr: #CBD5DF;
    border: 1px solid #CBD5DF;
    box-shadow: 0 2px 8px rgba(22, 32, 51, 0.08);
    padding: 2.5rem 2rem;
    text-align: left;
    color: var(--ink-2);
}

.empty-doc-lbl {
    font-size: 0.56rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.75rem;
}

.doc-hdr {
    background: var(--navy);
    padding: 1.875rem 2.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1.5rem;
}

.dhdr-prog {
    font-size: 0.56rem;
    font-weight: 700;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--orange);
    margin-bottom: 0.5rem;
}

.dhdr-name {
    font-family: var(--serif);
    font-size: 1.625rem;
    font-weight: normal;
    color: #FFF;
    line-height: 1.2;

    &.letter-title {
        font-size: 1.25rem;
    }
}

.dhdr-meta {
    margin-top: 0.4rem;
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.46);
    display: flex;
    gap: 1.25rem;
    flex-wrap: wrap;
}

.dhdr-r {
    text-align: right;
    flex-shrink: 0;
}

.dhdr-type-lbl {
    font-size: 0.48rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.28);
    margin-bottom: 0.25rem;
}

.dhdr-type {
    font-family: var(--serif);
    font-style: italic;
    font-size: 0.9375rem;
    color: rgba(255, 255, 255, 0.78);
}

.logo-mark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    padding: 0 7px;
    border-radius: 3px;
    background: #FFF;
    flex-shrink: 0;
    margin-bottom: 0.625rem;

    img {
        height: 20px;
        width: auto;
        display: block;
    }
}

.doc-gold-line {
    height: 2px;
    background: linear-gradient(to right, var(--track) 0%, var(--track) 42%, transparent 100%);
}

.page-sep {
    max-width: 720px;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 0.25rem 0;
}

.page-sep-line {
    flex: 1;
    height: 1px;
    background: var(--bdr);
}

.page-sep-text {
    font-size: 0.56rem;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--ink-4);
    white-space: nowrap;
}

.docPlaceholder {
    color: var(--ink-4);
    font-size: 0.875rem;
    line-height: 1.6;
    margin: 0;
}

.noCertificateTip {
    margin: 0;
    color: var(--ink-2);
    font-size: 0.9375rem;
    line-height: 1.6;
    font-family: var(--serif);
}

.empty-doc-sub {
    margin: 0.55rem 0 0;
    color: var(--ink-4);
    font-size: 0.8125rem;
    line-height: 1.5;
}

/* ── Certificate ── */
.cert-doc {
    max-width: 980px;
    border: none;
}

.cert-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1.5rem;
    padding: 0.15rem 0 1.1rem;
    border-bottom: none;
    position: relative;
    z-index: 1;

    .brand-wordmark {
        color: #FFF;
        font-size: 0.95rem;
        margin-top: 0.15rem;
    }
}

.cert-logo-track-text {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #FFF;
    opacity: 0.92;
    text-align: right;
    max-width: 40%;
    background: rgba(255, 255, 255, 0.12);
    padding: 0.35rem 0.55rem;
    border-radius: 3px;
}

.cert-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.5rem 0 0.75rem;
    position: relative;
    z-index: 1;
}

.cert-ey {
    font-size: 0.56rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--ink-3);
    margin-bottom: 0.65rem;
}

.cert-h1 {
    font-family: var(--sans);
    font-weight: 800;
    font-size: clamp(1.45rem, 2.5vw, 1.9rem);
    color: var(--navy);
    letter-spacing: -0.01em;
    margin-bottom: 0.85rem;
}

.cert-banner {
    margin-bottom: 1.5rem;
}

.cert-pt {
    font: italic 0.875rem / 1 var(--serif);
    color: var(--ink-3);
    margin-bottom: 0.4rem;
}

.cert-name {
    font: normal clamp(1.9rem, 3.4vw, 2.7rem) / 1.12 var(--serif);
    color: var(--navy);
    letter-spacing: 0.015em;
}

.cert-nu {
    width: 52px;
    height: 1px;
    background: var(--track);
    margin: 0.65rem auto 0.95rem;
}

.cert-br {
    font: italic 0.8125rem / 1 var(--serif);
    color: var(--ink-3);
    margin-bottom: 0.4rem;
}

.cert-proj {
    font: bold clamp(0.95rem, 1.5vw, 1.15rem) / 1.45 var(--serif);
    color: var(--navy);
}

.cert-award {
    width: 100%;
    max-width: 640px;
    margin-top: clamp(0.7rem, 1.2vw, 1rem);
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(243, 156, 18, 0.055);
    border: 1px solid rgba(243, 156, 18, 0.28);
    border-radius: 2px;
    padding: clamp(0.85rem, 1.4vw, 1.1rem) clamp(1.25rem, 2.6vw, 1.9rem);
}

.award-tier-lbl {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: rgba(243, 156, 18, 0.65);
    margin-bottom: 0.55rem;
}

.award-dots {
    display: flex;
    gap: 6px;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.6rem;
}

.award-dot {
    width: 8px;
    height: 8px;
    transform: rotate(45deg);
    flex-shrink: 0;

    &.filled {
        background: var(--orange);
    }

    &.empty {
        background: none;
        border: 1.5px solid rgba(243, 156, 18, 0.38);
    }
}

.award-name {
    font: normal clamp(1.2rem, 1.9vw, 1.45rem) / 1.2 var(--serif);
    color: var(--orange);
    margin-bottom: 0.6rem;
    letter-spacing: 0.02em;
}

.award-desc {
    font: italic clamp(0.75rem, 1.1vw, 0.82rem) / 1.68 var(--serif);
    color: var(--ink-3);
    max-width: 620px;
}

.cert-doc .brand-doc-ft {
    border-top: 1px solid var(--line);
    padding-top: 0.75rem;
    margin-top: 0.2rem;
}

/* ── Transcript ── */
.trans-doc {
    border: none;
}

.cert-qr-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
}

.cert-id-wrap {
    text-align: center;
}

.cert-id-val {
    font: 600 0.7rem / 1 var(--mono);
    color: var(--ink-2);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.cert-date-wrap {
    text-align: right;
}

.cert-date-lbl {
    font-size: 0.54rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.2rem;
}

.cert-date-val {
    font: 0.9rem / 1 var(--serif);
    color: var(--ink-2);
}

.cert-issuer {
    font-size: 0.52rem;
    color: var(--ink-4);
    margin-top: 0.1rem;
}

.trans-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.65rem;
    border: none;
    padding: 0;
    background: transparent;
    margin: 0.85rem 0 0.75rem;
}

.tstat {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 0.28rem;
    padding: 0.7rem 0.8rem;
    margin: 0;
    border: none;
    border-radius: 3px;
    background: #EEF2F7;
    min-width: 0;
    overflow: hidden;
}

.tstat-lbl {
    font-size: 0.52rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-3);
}

.tstat-val {
    font-family: var(--sans);
    font-weight: 800;
    font-size: 0.95rem;
    line-height: 1.2;
    color: var(--navy);
    font-variant-numeric: tabular-nums;
    font-style: normal;
}

.tstat-val-sm {
    font-family: var(--sans);
    font-weight: 800;
    font-size: 0.82rem;
    line-height: 1.25;
    color: var(--navy);
    overflow-wrap: anywhere;
}

.tstat-sub {
    font-size: 0.62rem;
    line-height: 1.35;
    color: var(--ink-2);
    overflow-wrap: anywhere;
}

.trans-body {
    padding: 1.35rem 2rem 0.5rem;
}

.tproj {
    border-left: 3px solid var(--track);
    padding: 0.75rem 1.125rem;
    margin-bottom: 1rem;
    background: rgba(243, 156, 18, 0.04);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1.25rem;
}

.tproj-main {
    min-width: 0;
}

.tproj-lbl {
    font-size: 0.53rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--track);
    margin-bottom: 0.3rem;
}

.tproj-title {
    font: 700 1.08rem / 1.42 var(--serif);
    color: #15181D;
}

.tproj-logo {
    height: clamp(24px, 4vw, 32px);
    width: auto;
    flex-shrink: 0;
    display: block;
    opacity: 0.92;
}

.tproj-logo-text {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--navy);
    opacity: 0.7;
    flex-shrink: 0;
    max-width: 28%;
    text-align: right;
}

.comp-intro {
    font: italic 0.76rem / 1.62 var(--serif);
    color: var(--ink-3);
    margin: 0 0 1.625rem;
}

.comp-section {
    margin-bottom: 1.375rem;
}

.comp-sec-hdr {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.875rem;
}

.comp-sec-lbl {
    font-size: 0.545rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-3);
}

.comp-sec-rule {
    flex: 1;
    height: 1px;
    background: var(--line);
}

.comp-sec-tag {
    font-size: 0.52rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 0.15rem 0.55rem;
    border-radius: 2px;
    white-space: nowrap;

    &.high {
        background: var(--sc-hbg);
        color: var(--sc-h);
    }
}

.comp-divider {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 1.375rem 0;
}

.comp-div-line {
    flex: 1;
    height: 1px;
    background: var(--line);
}

.comp-div-lbl {
    font-size: 0.53rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-4);
    white-space: nowrap;
}

.comp-row {
    margin-bottom: 0.825rem;

    &:last-child {
        margin-bottom: 0;
    }
}

.comp-row-top {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 0.32rem;
}

.comp-name {
    font-size: 0.84rem;
    font-weight: 500;
    color: var(--ink);
}

.comp-score-wrap {
    display: flex;
    align-items: baseline;
    gap: 0.55rem;
    flex-shrink: 0;
}

.comp-frac {
    font: 600 0.775rem / 1 var(--mono);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
}

.comp-pct {
    font-size: 0.61rem;
    color: var(--ink-3);
    font-variant-numeric: tabular-nums;
    min-width: 2.5rem;
    text-align: right;
}

.bar-track {
    height: 5px;
    border-radius: 3px;
    overflow: hidden;
    background: var(--sc-hbg);

    &.low {
        background: var(--sc-lbg);
    }
}

.bar-fill {
    height: 100%;
    border-radius: 3px;
    background: var(--sc-h);

    &.low {
        background: var(--sc-l);
    }
}

.bar-legend {
    display: flex;
    gap: 1rem;
    margin-top: 1.25rem;
    flex-wrap: wrap;
}

.bar-legend-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.575rem;
    color: var(--ink-3);
}

.bar-legend-swatch {
    display: inline-block;
    height: 4px;
    border-radius: 2px;
    flex-shrink: 0;

    &.track {
        width: 22px;
        background: var(--sc-hbg);
        border: 1px solid var(--bdr);
    }

    &.fill {
        width: 12px;
        background: var(--sc-h);
    }
}

.trans-credits {
    border-top: 1px solid var(--line);
    padding: 1.25rem 2rem;
    margin-top: auto;
}

.tcr-sec-lbl {
    font-size: 0.545rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--ink-3);
    margin-bottom: 0.75rem;
}

.tcr-name-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35rem 0.5rem;
    margin-bottom: 0.65rem;
}

.tcr-name {
    font-family: var(--sans);
    font-weight: 800;
    font-size: 0.95rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--navy);
}

.tcr-sep {
    color: var(--ink-4);
}

.tcr-role {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-3);
}

.tcr-body {
    font: italic 0.875rem / 1.74 var(--serif);
    color: var(--ink-2);
    white-space: pre-wrap;
    overflow: hidden;
    max-height: calc(0.875rem * 1.74 * 5);
}

.letter-head {
    position: absolute;
    z-index: 2;
    left: 12%;
    right: 12%;
    top: 8%;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding-bottom: 0.9rem;
    border-bottom: 2px solid #2F509F;
}

.letter-head-logo {
    width: 146px;
    height: auto;
    object-fit: contain;
}

.letter-head-meta {
    text-align: right;
    color: #515B68;
    font-size: 9pt;
    line-height: 1.45;
}

.letter-ft-dynamic {
    position: absolute;
    left: 12%;
    right: 12%;
    bottom: 7%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding-top: 0.9rem;
    border-top: 1px solid #E1E6EE;
}

.letter-ft-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.letter-ft-id {
    font: 400 8pt / 1.2 var(--mono);
    color: #6D7683;
}

.letter-ft-scan {
    margin-top: 0.18rem;
    font-size: 6.5pt;
    color: #7A8491;
    text-transform: uppercase;
}

.letter-ft-right-dynamic {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.4rem;
}

.letter-ft-logo {
    height: 30px;
    width: auto;
    object-fit: contain;
}

.trans-ft {
    border-top: 1px solid var(--line);
    margin: 0 2rem;
    padding: 1rem 0 0.35rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
    background: transparent;
}

.tft-qr-wrap {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.tft-qr-id {
    display: block;
    font: 600 0.625rem / 1 var(--mono);
    color: var(--ink-2);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.tft-qr-lbl {
    display: block;
    font-size: 0.5rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-top: 0.15rem;
}

.tft-date-wrap {
    text-align: right;
}

.tft-date-lbl {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.15rem;
}

.tft-date-val {
    font: 0.8125rem / 1 var(--serif);
    color: var(--ink-3);
}

.tft-issuer {
    font-size: 0.52rem;
    color: var(--ink-4);
    margin-top: 0.05rem;
}

.transcriptPage {
    page-break-after: always;
    break-after: page;
}

.transcriptPage2 {
    page-break-after: auto;
    break-after: auto;
}

/* ── Letter ── */
.letter-doc {
    border: none;
}

.letter-salutation {
    font-family: var(--sans);
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--navy);
    margin-bottom: 1.5rem;
}

.letter-para,
.letter-html-body {
    font: 0.9375rem / 1.78 var(--serif);
    color: var(--ink);
    margin: 0 0 1.75rem;
    text-align: justify;
}

.letter-html-body .letter-para {
    margin: 0 0 1.75rem;
}

.letter-html-body .letter-para:last-child {
    margin-bottom: 0;
}

.letter-para:last-of-type {
    margin-bottom: 0;
}

.letter-closing {
    margin-top: 1.5rem;
}

.letter-ft.brand-doc-ft {
    margin-top: 2rem;
}

.letter-regen-hint {
    margin: 8px 0 18px;
    padding: 10px 12px;
    background: #fff8e8;
    border-left: 3px solid var(--orange);
    color: var(--ink-2);
    font-size: 12px;
    line-height: 1.5;
}

.letter-closing {
    margin-top: 1.875rem;
}

.letter-closing-text {
    font: 0.9375rem / 1 var(--serif);
    color: var(--navy);
    margin-bottom: 1.75rem;
}

.letter-sig-block {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 1.5rem;
}

.letter-sig-rule {
    width: 110px;
    height: 1px;
    background: var(--ink-4);
    margin-bottom: 0.35rem;
}

.letter-sig-name {
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--navy);
    letter-spacing: 0.02em;
}

.letter-sig-org {
    font-size: 0.6rem;
    color: var(--ink-4);
    margin-top: 0.05rem;
}

.letter-seal {
    width: 86px;
    height: 86px;
    border: 2px solid var(--teal);
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 8px;
    flex-shrink: 0;
    background: #FFF;

    img {
        height: 28px;
        width: auto;
        display: block;
    }

    span {
        font: 700 0.55rem / 1 var(--sans);
        letter-spacing: 0.16em;
        color: var(--teal);
        text-transform: uppercase;
    }
}

.letter-ft {
    margin-top: 2rem;
    padding-top: 1.25rem;
    border-top: 1px solid var(--line);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.letter-track-logo {
    height: clamp(24px, 4vw, 32px);
    width: auto;
    flex-shrink: 0;
    display: block;
    opacity: 0.92;
}

.letter-qr-wrap {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.lqr-id {
    display: block;
    font: 600 0.625rem / 1 var(--mono);
    color: var(--ink-2);
    letter-spacing: 0.04em;
    font-variant-numeric: tabular-nums;
}

.lqr-lbl {
    display: block;
    font-size: 0.5rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-top: 0.15rem;
}

.letter-ft-r {
    text-align: right;
}

.letter-ft-date-lbl {
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--ink-4);
    margin-bottom: 0.15rem;
}

.letter-ft-date-val {
    font: 0.8125rem / 1 var(--serif);
    color: var(--ink-3);
}

@media (max-width: 720px) {
    .cert-inner {
        padding: 44px 26px 26px 58px;
    }

    .cert-head {
        flex-direction: column;
        align-items: center;
        gap: 0.7rem;
        text-align: center;
    }

    .cert-logo-track-text {
        max-width: 100%;
        text-align: center;
    }

    .cert-ft {
        grid-template-columns: 1fr;
        justify-items: center;
        row-gap: 1rem;
        text-align: center;
    }

    .cert-qr-wrap {
        align-items: center;
    }

    .cert-date-wrap {
        text-align: center;
    }

    .tproj {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.75rem;
    }

    .tproj-logo-text {
        max-width: 100%;
        text-align: left;
    }

    .trans-stats {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.5rem;
        padding: 0;
    }

    .tstat {
        border: none;
        margin: 0;
        padding: 0.7rem 0.8rem;
    }

    .trans-body,
    .trans-credits {
        padding-left: 0;
        padding-right: 0;
    }

    .doc-hdr {
        flex-direction: column;
        gap: 0.75rem;
        padding: 1.375rem 1.5rem;
    }

    .dhdr-r {
        text-align: left;
    }

    .letter-doc.letter-a4 {
        width: 100%;
        max-width: 210mm !important;
        height: auto;
        min-height: 0;
        aspect-ratio: 210 / 297;
    }

    .letter-doc.letter-a4 .letter-fill {
        left: 6.5%;
        right: 6.5%;
        top: 31%;
        bottom: 22%;
    }

    .trans-doc.transcript-a4 {
        width: 100%;
        max-width: 210mm !important;
        height: auto;
        min-height: 0;
        aspect-ratio: 210 / 297;
    }

    .trans-doc.transcript-a4 .trans-fill {
        left: 5.5%;
        right: 5.5%;
        top: 5.5%;
        bottom: 15%;
    }

    .trans-doc.transcript-a4 .trans-ft {
        left: 5.5%;
        right: 5.5%;
        bottom: 3.5%;
        padding-top: 0.8rem;
    }

    .letter-sig-block {
        flex-direction: column;
        align-items: flex-start;
        gap: 1.25rem;
    }
}

@media print {
    @page {
        size: A4;
        margin: 0;
    }

    .cred-nav,
    .page-sep,
    .bar-legend,
    .empty-doc-card,
    .cred-view[style*="display: none"] {
        display: none !important;
    }

    html,
    body {
        margin: 0 !important;
        padding: 0 !important;
        background: #FFF !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .cred-page {
        margin: 0;
        min-height: auto;
        background: #FFF;
    }

    .doc-stage {
        padding: 0;
        min-height: auto;
        background: #FFF;
        gap: 0;
    }

    .doc-card {
        box-shadow: none !important;
        max-width: 100%;
        page-break-inside: avoid;
        break-inside: avoid;
    }

    .brand-frame {
        width: 297mm !important;
        height: 210mm !important;
        max-width: 297mm !important;
        aspect-ratio: 297 / 210;
        margin: 0 !important;
    }

    .doc-bg-img {
        object-fit: fill;
    }

    .cert-doc,
    .letter-doc,
    .trans-doc {
        border: none;
    }

    .letter-doc.letter-a4 {
        width: 210mm !important;
        height: 297mm !important;
        max-width: 210mm !important;
        min-height: 297mm !important;
        margin: 0 !important;
        box-shadow: none !important;
        page-break-after: avoid;
        break-inside: avoid;
    }

    .trans-doc.transcript-a4 {
        width: 210mm !important;
        height: 297mm !important;
        max-width: 210mm !important;
        min-height: 297mm !important;
        margin: 0 auto !important;
        box-shadow: none !important;
        break-inside: avoid;
    }

    .transcriptPage {
        page-break-after: always;
        border: none;
        margin: 0;
    }

    .transcriptPage2 {
        page-break-before: always;
        page-break-after: auto;
    }
}

/* Round 3: fixed document geometry is identical in preview and print. */
.cred-page { margin:0; min-height:100vh; }
.cred-nav { position:relative; top:auto; }
.trans-doc.transcript-a4 { width:210mm !important; min-width:210mm; height:297mm !important; min-height:297mm !important; }
.trans-doc.transcript-a4 .trans-fill { top:0; bottom:40mm; left:14mm; right:14mm; padding:0; overflow:visible; }
.trans-doc.transcript-a4 .ach-hdr { height:34mm; flex:0 0 34mm; box-sizing:border-box; padding:8mm 0 0; }
.trans-doc.transcript-a4 .ach-hdr-bar { padding:0; display:flex; flex-direction:column; align-items:flex-start; gap:2mm; }
.trans-doc.transcript-a4 .ach-hdr-title { font-size:17pt; color:white; }
.trans-doc.transcript-a4 .ach-hdr-sub { font-size:9pt; color:white; }
.trans-doc.transcript-a4 .ach-identity { padding:4mm 0 2mm; }
.trans-doc.transcript-a4 .trans-stats { display:grid !important; grid-template-columns:repeat(3,minmax(0,1fr)) !important; gap:3mm; margin:3mm 0; }
.trans-doc.transcript-a4 .tstat { margin:0; padding:2mm; }
.trans-doc.transcript-a4 .trans-body { flex:0 0 auto; min-height:0; overflow:visible; }
.trans-doc.transcript-a4 .comp-row { margin-bottom:3mm; }
.trans-doc.transcript-a4 .trans-credits { margin-top:auto; padding:3mm 0 0; flex-shrink:0; }
.trans-doc.transcript-a4 .tcr-body { font:normal 10.5pt/1.4 Georgia,serif; white-space:normal; overflow:visible; max-height:none; }
.trans-doc.transcript-a4 .trans-ft { bottom:14mm; left:14mm; right:14mm; padding:2mm 0 0; align-items:flex-start; }
.trans-doc.transcript-a4 .brand-ft-left, .trans-doc.transcript-a4 .brand-ft-meta { align-items:flex-start; margin-top:0; padding-top:0; }
.assessment-identity { padding:5mm 0; font-size:10pt; }
.assessment-list { display:flex; flex-direction:column; gap:2mm; }
.assessment-row { display:grid; grid-template-columns:minmax(0,1fr) 25mm 15mm; gap:1mm 3mm; font-size:9pt; line-height:1.3; break-inside:avoid; }
.assessment-row > strong, .assessment-row > span:nth-child(2) { text-align:right; }
.assessment-bar { grid-column:1/-1; height:1.2mm; background:#e9eef7; }
.assessment-bar > div { height:100%; background:#2F509F; }
.letter-doc.letter-a4 { width:210mm !important; min-width:210mm; height:297mm !important; min-height:297mm !important; }
.letter-doc.letter-a4 .letter-fill { left:6.5%; right:6.5%; top:66mm; bottom:57mm; overflow:visible; color:#000; }
.letter-doc.letter-a4 .letter-fill, .letter-doc.letter-a4 .letter-fill * { color:#000; font-family:Georgia,serif; }
.letter-doc.letter-a4 .letter-title { margin-bottom:6mm; }
.letter-doc.letter-a4 .letter-title h1 { font-size:20pt; line-height:1.2; margin:0 0 2mm; }
.letter-doc.letter-a4 .letter-title p { font-size:11pt; margin:0; }
.letter-doc.letter-a4 .letter-salutation, .letter-doc.letter-a4 .letter-para, .letter-doc.letter-a4 .letter-html-body, .letter-doc.letter-a4 .letter-closing-text { font-size:10.5pt; line-height:1.45; font-weight:normal; }
.letter-doc.letter-a4 .letter-salutation, .letter-doc.letter-a4 .letter-para { margin:0 0 3mm; }
.letter-doc.letter-a4 .letter-closing { margin-top:0; }
.letter-doc.letter-a4 .letter-html-body, .letter-doc.letter-a4 .letter-para { text-align:left; }
.letter-doc.letter-a4 .letter-sig-block { display:flex; flex-direction:row; justify-content:space-between; align-items:flex-end; margin-top:4mm; }
.letter-doc.letter-a4 .letter-sig-name, .letter-doc.letter-a4 .letter-sig-org { font-size:10.5pt; }
.letter-doc.letter-a4 .letter-seal { box-sizing:border-box; width:86px; height:86px; border-color:#000; }
.letter-doc.letter-a4 .letter-ft-dynamic { left:6.5%; right:6.5%; bottom:7%; color:#000; display:grid; grid-template-columns:1fr auto 1fr; align-items:start; gap:4mm; }
.letter-doc.letter-a4 .letter-ft-dynamic * { color:#000; }
.letter-doc.letter-a4 .letter-ft-left { align-items:flex-start; gap:2mm; }
.letter-doc.letter-a4 .letter-ft-right-dynamic { font-size:8pt; text-align:right; }
@media print {
  .trans-doc.transcript-a4 .trans-stats { grid-template-columns:repeat(3,minmax(0,1fr)) !important; }
  .transcriptPage2 { break-before:page; page-break-before:always; }
  .transcriptPage { break-after:auto; page-break-after:auto; }
}
</style>

<style scoped>
.assessment-identity { display:flex; justify-content:space-between; gap:4mm; }
</style>

<style>
.assessment-meta { width:100%; border-collapse:collapse; table-layout:fixed; margin:5mm 0 0; font:9pt/1.4 Arial,sans-serif; color:#26364d; }
.assessment-meta td { border:1px solid #dce3ed; padding:2.5mm 3mm; vertical-align:top; overflow-wrap:anywhere; }
.assessment-meta td span { display:block; color:#5e6c81; font-size:8pt; margin-bottom:1mm; }
.assessment-meta tr:last-child td span { display:inline; margin-right:3mm; }
.transcriptPage2 .assessment-identity { padding:4mm 0 3mm; font:10pt Arial,sans-serif; }
.cred-nav-print:disabled { cursor:wait; opacity:.5; }
</style>

<style>
.letter-wave-art { position:absolute; inset:0; width:100%; height:100%; pointer-events:none; }
.letter-brand-lockup { position:absolute; top:32mm; left:6.5%; right:6.5%; }
.letter-doc.letter-a4 .letter-title { margin-bottom:7mm; }
.letter-doc.letter-a4 .letter-title h1 { color:#17366c; }
.letter-doc.letter-a4 .letter-title p { font:10pt/1.4 Arial,sans-serif; color:#53627b; }
.letter-doc.letter-a4 .letter-ft-dynamic { bottom:22mm; display:flex; align-items:flex-end; justify-content:space-between; padding-top:5mm; }
.letter-doc.letter-a4 .letter-verification-meta { align-self:center; padding-bottom:1mm; text-align:right; }
.letter-doc.letter-a4 .letter-ft-date-label { font:9pt/1.4 Arial,sans-serif; color:#53627b; text-transform:uppercase; letter-spacing:.06em; }
.letter-doc.letter-a4 .letter-ft-date-val { font:11pt/1.5 Georgia,serif; color:#17366c; margin:1mm 0 3mm; }
.letter-doc.letter-a4 .letter-ft-id { font:7pt/1.5 Arial,sans-serif; color:#53627b; overflow-wrap:anywhere; }
.letter-certified-stamp { width:25mm; text-align:center; flex:none; }
.letter-doc.letter-a4 .letter-certified-title { font:600 8pt/1.4 Arial,sans-serif; color:#17366c; letter-spacing:.08em; text-transform:uppercase; margin-bottom:1mm; }
.letter-certified-stamp .digital-certificate-qr { width:25mm; }
@media print { .letter-wave-art { print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
</style>

<style scoped>
.tcr-body, .letter-para { overflow-wrap: anywhere; }
</style>
